import { _, helpers, mui, React, ts, ui, useSelector } from '_core';

import { FinDataGrid } from '@local/finsera-core/src/_components';

import { Params, TabsEnum } from './types';
import { formatData, prepareLinearData } from '../basket-compare-cumulative-returns/helpers';

const tableColumns: mui.dataGrid.GridColDef[] = [
  {
    field: 'name',
    headerName: 'Name',
    type: 'string',
    flex: 1,
  },
  {
    field: 'basket_weight',
    headerName: 'ETF Basket Weight',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: 'optimal_weight',
    headerName: 'Optimal Basket Weight',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '5_day_return',
    headerName: '5-Day Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '1_month_return',
    headerName: '1-Month Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '3_month_return',
    headerName: '3-Month Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '6_month_return',
    headerName: '6-Month Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '1_year_return',
    headerName: '1-Year Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '3_year_return',
    headerName: '3-Year Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
  {
    field: '5_year_return',
    headerName: '5-Year Returns',
    type: 'number',
    valueFormatter: helpers.tableFormatters.toPercentage,
    flex: 0.25,
  },
];

type ChartProps = {
  id: string;
  loadWidgetData: (_payload: ts.types.widgets.WidgetGetDataParams) => Promise<ts.types.widgets.WidgetGetDataResponse>;
  params: Params;
  setParams: (_p: Params) => void;
  fullScreen: boolean;
  goFullScreen: (_v: string) => void;
  context: ts.types.backtest.BacktestAnaylsisContext | ts.types.basket.BasketAnalysisContext;
  detailsFile: string;
  performanceFile: string;
  queryParams?: { initial?: string };
};

const Chart = ({ id, loadWidgetData, detailsFile, performanceFile, fullScreen }: ChartProps) => {
  const container = React.createRef();
  const [tableData, setTableData] = React.useState<ts.types.widgets.TableData>([]);
  const [chartData, setChartData] = React.useState<ts.types.widgets.TableData>([]);
  const [selectedTab, setSelectedTab] = React.useState<TabsEnum>(TabsEnum.DETAILS);
  const [alert, setAlert] = React.useState<{ type: 'error' | 'warning'; message: string } | null>(null);
  const baskets = useSelector((state) => state.resources.baskets);

  React.useEffect(() => {
    if (detailsFile && performanceFile) {
      loadTableData();
    }
    return () => setTableData([]);
  }, [detailsFile]);

  React.useEffect(() => {
    if (performanceFile) {
      loadChartData();
    }
    return () => setChartData([]);
  }, [performanceFile]);

  const loadTableData = async () => {
    try {
      const resp = await loadWidgetData({
        data: {
          file: detailsFile,
        },
      });
      if (resp) {
        setTableData(resp.data);
      }
    } catch (err) {
      setAlert({
        type: 'error',
        message: helpers.parseApiError(err as ts.types.common.ApiError),
      });
    }
  };

  const loadChartData = async () => {
    try {
      const resp = await loadWidgetData({
        data: {
          file: performanceFile,
        },
      });
      if (resp) {
        const formattedData = formatData(resp.data, Object.values(ts.enums.PERIOD_ENUM));
        setChartData(prepareLinearData(formattedData, true, baskets));
      }
    } catch (err) {
      setAlert({
        type: 'error',
        message: helpers.parseApiError(err as ts.types.common.ApiError),
      });
    }
  };

  const renderChart = () => (
    <>
      <mui.core.Tabs
        indicatorColor="primary"
        textColor="primary"
        value={selectedTab}
        onChange={(_e, newTab) => {
          return setSelectedTab(newTab);
        }}
      >
        <mui.core.Tab label={TabsEnum.DETAILS} value={TabsEnum.DETAILS} />
        <mui.core.Tab label={TabsEnum.PERFORMANCE} value={TabsEnum.PERFORMANCE} />
      </mui.core.Tabs>
      {selectedTab == TabsEnum.DETAILS && (
        <FinDataGrid
          rows={tableData}
          groupingColDef={{
            headerName: 'Ticker',
            valueGetter: (_value, row) => row.ticker,
            flex: 0.25,
          }}
          getRowId={(row) => row.path}
          columns={tableColumns}
          treeData={true}
          getTreeDataPath={(row) => {
            return row.path.split('/');
          }}
          initialState={{
            sorting: {
              sortModel: [{ field: 'basket_weight', sort: 'desc' }],
            },
          }}
        />
      )}
      {selectedTab == TabsEnum.PERFORMANCE && (
        <>
          <ui.LineChart
            id={id}
            data={chartData}
            alert={alert}
            numberFormat="#.##%"
            fullScreen={fullScreen}
            readOnly={false}
            showEndValueLabel
            showPeriodSelector
          />
        </>
      )}
    </>
  );

  return (
    <mui.core.Box display="flex" flexDirection="column" style={{ height: '100%' }} ref={container}>
      {!_.isNil(detailsFile) && !_.isNil(performanceFile) && renderChart()}
    </mui.core.Box>
  );
};

export default Chart;
